import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clampToMax, stockLabel, stockOfSupplier, stockProblem } from './stock';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { priceLine } from '@app/shared/price-calc';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { BarterWarning } from '../barter/BarterWarning';
import { packlistKey } from '../packlists/types';
import { PEOPLE_KEY, PERSONS_RESOURCE } from '../people/types';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { MANUAL_STATUSES, PRICES_RESOURCE, RENTAL_STATUSES, RENTALS_RESOURCE, SUBRENTALS_KEY, type CostMode, type MatchCandidate, type MatchResult, type PriceView, type RentalPreset, type RentalStatus, type RentalView } from './types';
import { rentalFormUrl, rentalPickUrl } from './rentalPick';
import { SupplierLacksItem } from './SupplierLacksItem';
import { candidateName, SupplierPositionPicker } from './SupplierPositionPicker';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useUnsavedGuard } from '../ui/unsaved';
import { formGroup, GROUP_FIELDS } from './groups';
import { NotesInput } from '../ui/NotesInput';
import { RentalMarkTag } from './RentalMarkTag';
import { RentalSplit, type SplitOverride } from './RentalSplit';
import { defaultSplit, type SplitRow } from './split';
import { roomTitle, type RoomView } from './room';
import { AvailabilityEditor } from './AvailabilityEditor';

type Supplier = { id: string; name: string };
type Person = { id: string; name: string };
type ItemOption = PickerOption & { id: string };

type Draft = {
  itemId: string;
  itemName: string;
  supplierId: string;
  quantity: string;
  firstDay: string;
  lastDay: string;
  costMode: CostMode;
  cost: string;
  status: RentalStatus;
  returnPersonId: string;
  notes: string;
  /** Owner 2026-10-24 16:15 (pkt 4): „Inny (spoza listy)” — the supplier's typed name and contact, the gear's name / description. */
  offList: boolean;
  offSupplierName: string;
  name: string;
  description: string;
  contactPhone: string;
  contactEmail: string;
  contactPerson: string;
};
/** The supplier chooser's value of „Inny (spoza listy)”. */
const OFF = '__off__';
/** Back from „Inny (spoza listy)” to a supplier of the list: its cost from the catalog again. */
const leaveOffList = (d: Pick<Draft, 'offList'>): Partial<Draft> => (d.offList ? { offList: false, costMode: 'cennik' } : {});

const draftOf = (r?: RentalView, preset?: RentalPreset): Draft => ({
  ...emptyDraft(r, preset),
  // review I2: what was typed before „Wskaż w katalogu” comes back with the form (a new rental only)
  ...(r ? {} : carriedDraft(preset?.carried)),
});
const carriedDraft = (c: RentalPreset['carried']): Partial<Draft> => {
  if (!c) return {};
  const out: Partial<Draft> = {};
  if (c.supplierId) out.supplierId = c.supplierId;
  if (c.notes !== undefined) out.notes = c.notes;
  if (c.status && (MANUAL_STATUSES as readonly string[]).includes(c.status)) out.status = c.status as RentalStatus;
  if (c.returnPersonId !== undefined) out.returnPersonId = c.returnPersonId;
  if (c.costMode) out.costMode = c.costMode;
  if (c.cost !== undefined) out.cost = c.cost;
  return out;
};
/** What the person typed in a new rental's form besides the shortage's item, quantity and days (review I2). */
function carriedOf(d: Draft): NonNullable<RentalPreset['carried']> {
  const empty = emptyDraft();
  const out: NonNullable<RentalPreset['carried']> = {};
  if (d.supplierId !== empty.supplierId) out.supplierId = d.supplierId;
  if (d.notes !== empty.notes) out.notes = d.notes;
  if (d.status !== empty.status) out.status = d.status;
  if (d.returnPersonId !== empty.returnPersonId) out.returnPersonId = d.returnPersonId;
  // „Inny (spoza listy)” is not carried: back from the catalog a supplier's position is chosen (its cost from the catalog)
  if (d.costMode !== empty.costMode && !d.offList) out.costMode = d.costMode;
  if (d.cost !== empty.cost && !d.offList) out.cost = d.cost;
  return out;
}
const emptyDraft = (r?: RentalView, preset?: RentalPreset): Draft => ({
  itemId: r?.itemId ?? preset?.itemId ?? '',
  itemName: r?.itemName ?? preset?.itemName ?? '',
  supplierId: r?.supplierId ?? '',
  quantity: String(r?.quantity ?? preset?.quantity ?? 1),
  firstDay: r?.firstDay ?? preset?.firstDay ?? '',
  lastDay: r?.lastDay ?? preset?.lastDay ?? '',
  costMode: r?.costMode ?? 'cennik',
  cost: r?.costGrosze !== undefined && r.costMode === 'reczny' ? plnToInput(r.costGrosze) : '',
  status: r?.status ?? 'planowane',
  returnPersonId: r?.returnPersonId ?? '',
  notes: r?.notes ?? '',
  offList: r?.offList ?? false,
  offSupplierName: r?.offSupplierName ?? '',
  name: r?.name ?? '',
  description: r?.description ?? '',
  contactPhone: r?.contactPhone ?? '',
  contactEmail: r?.contactEmail ?? '',
  contactPerson: r?.contactPerson ?? '',
});

/** The fields of a supplier's position a candidate has (those the role sees). */
function pick(p: PriceView): Omit<MatchCandidate, 'priceId' | 'match'> {
  const out: Omit<MatchCandidate, 'priceId' | 'match'> = {};
  if (p.supplierId !== undefined) out.supplierId = p.supplierId;
  if (p.supplierName !== undefined) out.supplierName = p.supplierName;
  if (p.itemId !== undefined) out.itemId = p.itemId;
  if (p.itemName !== undefined) out.itemName = p.itemName;
  if (p.name !== undefined) out.name = p.name;
  if (p.priceDayGrosze !== undefined) out.priceDayGrosze = p.priceDayGrosze;
  if (p.quantity !== undefined) out.quantity = p.quantity;
  return out;
}

/** How long the server takes to follow a rental in the packlist (its outbox runs every half a second). */
const PACKLIST_FOLLOW_MS = 1500;

const dayCount = (first: string, last: string): number => Math.round((Date.parse(`${last}T00:00:00Z`) - Date.parse(`${first}T00:00:00Z`)) / 86_400_000) + 1;

async function searchItems(q: string, signal: AbortSignal): Promise<ItemOption[]> {
  const res = await api<{ items: { id: string; name: string; manufacturer?: string; model?: string }[] }>(`/api/catalog/items?q=${encodeURIComponent(q)}`, { signal });
  return res.items.slice(0, 20).map((i) => ({ key: i.id, id: i.id, name: i.name, details: [[i.manufacturer, i.model].filter(Boolean).join(' ')].filter(Boolean) }));
}

/**
 * "Dopożycz" (F28): add (no `rental`) or edit a rental of the event. Only fields the role may see are shown and sent;
 * on edit only the changed ones (rule 7a). With `preset` (from a shortage of the packlist) the item, quantity and days
 * are filled in. The cost "z katalogu dostawcy" is counted by the server; the line under it is the same calculation.
 */

/**
 * `group`: the other rentals of the same supplier in the event (versions as seen); with any, the form offers
 * „Zmień dla wszystkich pozycji od <dostawca> w tym wydarzeniu”, checked — who gives the gear back, the days, the status
 * and the notes then change for all of them in one save (the item, quantity and cost never).
 */
/**
 * Owner 2026-10-23 21:23 („Dopożycz” of a new rental of our item): `SupplierPositionPicker` finds the suppliers'
 * positions for the item and chooses the clear one; the cost comes from the chosen position. `picked`: the position
 * pointed at in „Wskaż w katalogu” (back from the suppliers' catalogs).
 */
export function RentalForm({
  eventId,
  rental,
  preset,
  picked,
  group = [],
  onClose,
}: {
  eventId: string;
  rental?: RentalView;
  preset?: RentalPreset;
  picked?: { supplierId: string; priceId: string } | null;
  group?: Array<{ id: string; version: number }>;
  onClose: () => void;
}) {
  const { me } = useMe();
  const location = useLocation();
  // the chosen position of a supplier's catalog (its price counts) and „Połącz … z moim …”
  const [chosen, setChosen] = useState<MatchCandidate | null>(null);
  const [link, setLink] = useState(true);
  // what the matching chose by itself (not a change of the person's) and the position pointed at in the catalog, once
  const [autoChoice, setAutoChoice] = useState<{ supplierId: string; priceId: string } | null>(null);
  const [pointedDone, setPointedDone] = useState(false);
  const [pointedNote, setPointedNote] = useState('');
  const queryClient = useQueryClient();
  const [base, setBase] = useState(rental);
  const [draft, setDraft] = useState<Draft>(() => draftOf(rental, preset));
  const [invalid, setInvalid] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [forAll, setForAll] = useState(true);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  // review I3: a new supplier takes the rental out of its order — no „dla wszystkich pozycji” then
  const supplierChanged = !!base && base.supplierId !== undefined && draft.supplierId !== base.supplierId;
  const sendGroup = (fields: Record<string, unknown>) => formGroup(fields, forAll, group, supplierChanged);
  // review N1/N2, I-new-1: a rental the quote controls (Planowane/Potwierdzone, still in its governing version) — its
  // item, quantity, supplier and cost are changed in the quote only. Picked up, returned, outside the governing version or
  // of a deleted quote: changed and deleted here; outside or deleted also "Odłącz od kosztorysu".
  const kept = !!base?.quoteControlled;
  const detachable = !!base?.keptByQuote && !!base.outsideQuote;
  const KEPT = ['itemId', 'name', 'quantity', 'supplierId', 'costMode', 'costGrosze'];
  const sees = (f: string) => !!me && fieldVisible(me, RENTALS_RESOURCE, f) && !(kept && KEPT.includes(f));
  const seeCost = sees('costGrosze');
  const seePrices = !!me && can(me, PRICES_RESOURCE, 'view') && fieldVisible(me, PRICES_RESOURCE, 'priceDayGrosze');

  const suppliers = useQuery({
    queryKey: ['contractors', 'suppliers'],
    enabled: !!me && can(me, 'contractors.contractors', 'view'),
    queryFn: () => api<{ contractors: Supplier[] }>('/api/contractors?kind=supplier'),
  });
  const persons = useQuery({
    queryKey: [...PEOPLE_KEY, 'persons', 'all'],
    enabled: !!me && can(me, PERSONS_RESOURCE, 'view') && sees('returnPersonId'),
    queryFn: () => api<{ persons: Person[] }>('/api/people/persons'),
  });
  // the suppliers' prices of the chosen item: the cost "z katalogu dostawcy" and which supplier has one
  const prices = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'prices', 'item', draft.itemId],
    enabled: seePrices && draft.itemId !== '',
    queryFn: () => api<{ prices: PriceView[] }>(`/api/subrentals/prices?itemId=${draft.itemId}`),
  });
  const priceOf = (supplierId: string) => prices.data?.prices.find((p) => p.supplierId === supplierId)?.priceDayGrosze;
  // „Pozycja dostawcy” (a new rental of our item): whoever may see the suppliers' catalogs and the rental's supplier
  const matching = !rental && !!me && can(me, PRICES_RESOURCE, 'view') && fieldVisible(me, RENTALS_RESOURCE, 'supplierId') && fieldVisible(me, RENTALS_RESOURCE, 'itemId') && draft.itemId !== '';
  const choose = useCallback((c: MatchCandidate | null, how: 'auto' | 'person') => {
    setChosen(c);
    setLink(true);
    if (how === 'auto' && c?.supplierId) setAutoChoice({ supplierId: c.supplierId, priceId: c.priceId });
    setPointedNote('');
    if (c?.supplierId) setDraft((d) => ({ ...d, supplierId: c.supplierId!, ...leaveOffList(d) }));
  }, []);
  // back from „Wskaż w katalogu”: the position pointed at, read from its supplier's catalog
  const pointed = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'prices', 'supplier', picked?.supplierId ?? ''],
    enabled: matching && !!picked && !pointedDone,
    queryFn: () => api<{ prices: PriceView[] }>(`/api/subrentals/prices?supplierId=${encodeURIComponent(picked!.supplierId)}`),
  });
  const pointedPosition = pointed.data?.prices.find((p) => p.id === picked?.priceId);
  // review I1: the position the person pointed at always wins (the matching does not choose then, see `auto` below)
  if (matching && picked && !pointedDone && pointed.data) {
    setPointedDone(true);
    if (!pointedPosition) setPointedNote('Wskazanej pozycji nie ma już w katalogu dostawcy — wybierz inną.');
    else if (pointedPosition.itemId != null && pointedPosition.itemId !== draft.itemId) {
      // review C1: a position of another item of ours never prices this one
      setPointedNote(`Tej pozycji nie można wybrać: jest podpięta pod ${pointedPosition.itemName ? `„${pointedPosition.itemName}”` : 'inne urządzenie'}. Wybierz inną albo wpisz koszt ręcznie.`);
    } else {
      const c: MatchCandidate = { priceId: pointedPosition.id, ...pick(pointedPosition), match: pointedPosition.itemId === draft.itemId ? 'exact' : 'similar' };
      setChosen(c);
      if (c.supplierId && draft.supplierId !== c.supplierId) setDraft((d) => ({ ...d, supplierId: c.supplierId!, ...leaveOffList(d) }));
    }
  }
  const ofChosen = !!chosen && chosen.supplierId === draft.supplierId;
  // owner 2026-10-24 10:40: a new rental of our item from several suppliers at once („ileś od jednego, ileś od drugiego”)
  // back from „Wskaż w katalogu” with a position pointed at: that one supplier's form
  const [single, setSingle] = useState(!!picked);
  const [splitQty, setSplitQty] = useState<Map<string, number>>(new Map());
  const [splitTouched, setSplitTouched] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, SplitOverride>>({});
  const [links, setLinks] = useState<Set<string>>(new Set());
  const splitAsked = !rental && !!me && can(me, RENTALS_RESOURCE, 'create') && fieldVisible(me, RENTALS_RESOURCE, 'supplierId') && fieldVisible(me, RENTALS_RESOURCE, 'quantity') && draft.itemId !== '' && !!draft.firstDay && !!draft.lastDay && draft.lastDay >= draft.firstDay;
  const splitKey = [...SUBRENTALS_KEY, 'split', eventId, draft.itemId, draft.firstDay, draft.lastDay];
  const splitData = useQuery({
    queryKey: splitKey,
    enabled: splitAsked,
    queryFn: () => api<{ suppliers: SplitRow[] }>(`/api/subrentals/events/${eventId}/split?itemId=${encodeURIComponent(draft.itemId)}&firstDay=${draft.firstDay}&lastDay=${draft.lastDay}`),
  });
  // owner 2026-10-24 16:15 (pkt 2): „max N” = what the supplier position still has on these days across all our events
  // (rule 7a: only for whoever sees the suppliers' quantities; the server answers {} otherwise)
  const roomPriceId = ofChosen ? chosen!.priceId : '';
  const roomAsked =
    !!me && can(me, PRICES_RESOURCE, 'view') && fieldVisible(me, PRICES_RESOURCE, 'quantity') && draft.supplierId !== '' && (draft.itemId !== '' || roomPriceId !== '' || !!base?.priceId) && !!draft.firstDay && !!draft.lastDay && draft.lastDay >= draft.firstDay;
  const roomQuery = new URLSearchParams({
    supplierId: draft.supplierId,
    firstDay: draft.firstDay,
    lastDay: draft.lastDay,
    ...(draft.itemId ? { itemId: draft.itemId } : {}),
    ...(roomPriceId ? { priceId: roomPriceId } : base?.priceId && base.supplierId === draft.supplierId ? { priceId: base.priceId } : {}),
    ...(base ? { rentalId: base.id } : {}),
  }).toString();
  const room = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'room', eventId, roomQuery],
    enabled: roomAsked,
    queryFn: () => api<RoomView>(`/api/subrentals/events/${eventId}/room?${roomQuery}`),
  });
  const splitRows = splitAsked ? (splitData.data?.suppliers ?? []) : [];
  const splitPossible = splitRows.some((r) => r.match === 'exact');
  const splitOn = splitPossible && !single;
  const need = Number(draft.quantity);
  const needOk = Number.isInteger(need) && need > 0;
  // the default split until the person types a quantity of their own (again after new numbers from the server)
  const splitSignature = splitRows.map((r) => `${r.priceId}:${r.available ?? '?'}`).join(',');
  useEffect(() => {
    if (splitTouched || !splitPossible) return;
    setSplitQty(defaultSplit(splitRows, needOk ? need : 0).quantities);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the rows are named by their signature
  }, [splitSignature, need, needOk, splitTouched, splitPossible]);
  // owner 2026-10-24 16:15 (pkt 4): no supplier on the list has the item — the window opens with „Inny (spoza listy)”
  // (once, before the person picks a supplier; only for whoever sees the suppliers' catalogs, who can know it)
  const offDefaulted = useRef(false);
  // the same matching as „Pozycja dostawcy” (shared cache): no position linked to the item nor of a similar name
  const matched = useQuery({
    queryKey: [...SUBRENTALS_KEY, 'match', draft.itemId, eventId],
    enabled: matching,
    queryFn: () => api<MatchResult>(`/api/subrentals/match?itemId=${encodeURIComponent(draft.itemId)}&eventId=${encodeURIComponent(eventId)}`),
  });
  // review L1: „Inny (spoza listy)” only for whoever may write it (hidden with the supplier field)
  const offAllowed = !!me && fieldVisible(me, RENTALS_RESOURCE, 'offList');
  const nobodyHasIt =
    offAllowed && matching && seePrices && !!prices.data && prices.data.prices.length === 0 && !!matched.data && matched.data.candidates.every((c) => c.match === 'other_item') && !splitPossible && !chosen && (!splitAsked || splitData.isSuccess);
  useEffect(() => {
    if (offDefaulted.current || !nobodyHasIt || draft.supplierId !== '' || draft.offList) return;
    offDefaulted.current = true;
    setDraft((d) => ({ ...d, offList: true, costMode: 'reczny' }));
  }, [nobodyHasIt, draft.supplierId, draft.offList]);
  // ⚙ open at once when the contact is still to fill in („do uzupełnienia”)
  const [gear, setGear] = useState(() => !!rental?.toFill?.includes('kontakt'));
  // review: one POST at a time — a second Enter / click or the unsaved guard's save never sends it again
  const splitBusy = useRef(false);
  const splitSave = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<{ rentals: RentalView[] }>(`/api/subrentals/events/${eventId}/rentals/split`, { method: 'POST', body }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }), queryClient.invalidateQueries({ queryKey: packlistKey(eventId) })]);
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }), PACKLIST_FOLLOW_MS);
      onClose();
    },
    // stale numbers (someone rented meanwhile): nothing saved — the list shows what is free now, the person decides again
    onError: async () => {
      await queryClient.invalidateQueries({ queryKey: splitKey });
      setSplitTouched(false);
    },
  });

  const save = useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      base
        ? api<RentalView>(`/api/subrentals/rentals/${base.id}`, {
            method: 'PATCH',
            body: { version: base.version, ...fields, ...(sendGroup(fields) ? { group: sendGroup(fields) } : {}) },
          })
        : api<RentalView>(`/api/subrentals/events/${eventId}/rentals`, { method: 'POST', body: fields }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }), queryClient.invalidateQueries({ queryKey: packlistKey(eventId) })]);
      // the packlist sets its reservations again in the background (a failed hard one becomes hard): read it once more
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: packlistKey(eventId) }), PACKLIST_FOLLOW_MS);
      onClose();
    },
  });
  const detach = useMutation({
    mutationFn: () => api<RentalView>(`/api/subrentals/rentals/${base!.id}/detach`, { method: 'POST', body: { version: base!.version } }),
    onSuccess: async (fresh) => {
      setBase(fresh);
      await queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY });
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/subrentals/rentals/${base!.id}?version=${base!.version}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY }), queryClient.invalidateQueries({ queryKey: packlistKey(eventId) })]);
      onClose();
    },
  });
  // owner 2026-10-23 21:14: leaving with unsaved changes (Escape, another event, a link) asks first
  const saveNow = useRef<() => Promise<boolean>>(async () => false);
  // the supplier the matching chose by itself is not a change of the person's (closing does not ask about it)
  // review: a position chosen by the person counts as a change too
  const plainPreset: RentalPreset | undefined = preset ? { itemId: preset.itemId, itemName: preset.itemName, quantity: preset.quantity, firstDay: preset.firstDay, lastDay: preset.lastDay } : undefined;
  const dirty = !sameValues(draft, { ...draftOf(base, plainPreset), ...(autoChoice ? { supplierId: autoChoice.supplierId } : {}), ...(offDefaulted.current && !base ? { offList: true, costMode: 'reczny' as const } : {}) }) || (chosen !== null && chosen.priceId !== autoChoice?.priceId) || (splitOn && splitTouched);
  const guardRef = useUnsavedGuard({ dirty, save: () => saveNow.current() });
  if (!me) return null;

  const quantity = Number(draft.quantity);
  const days = draft.firstDay && draft.lastDay && draft.lastDay >= draft.firstDay ? dayCount(draft.firstDay, draft.lastDay) : null;
  const supplierPrice = ofChosen && chosen!.priceDayGrosze !== undefined ? chosen!.priceDayGrosze : draft.supplierId ? priceOf(draft.supplierId) : undefined;
  // „Połącz … z moim …”: a position only the supplier has, for whoever may change the suppliers' catalogs (7a: its field)
  const mayLink = ofChosen && chosen!.itemId === null && chosen!.linkable !== false && can(me, PRICES_RESOURCE, 'edit') && fieldVisible(me, PRICES_RESOURCE, 'itemId');
  // review C1: the supplier has another position of this item already — say why there is no „Połącz”
  const noLink = ofChosen && chosen!.itemId === null && chosen!.linkable === false && can(me, PRICES_RESOURCE, 'edit');
  // the same calculation as the server's (`priceLine`, linear): a preview while the form is filled in
  const estimate = supplierPrice !== undefined && days !== null && Number.isInteger(quantity) && quantity > 0 ? priceLine({ unitPriceGrosze: supplierPrice, quantityMilli: quantity * 1000, daysMilli: days * 1000, linear: true, discountBp: 0 }, [10000]) : null;

  const supplierName = (id: string) => (suppliers.data?.contractors ?? []).find((x) => x.id === id)?.name ?? (base?.supplierId === id ? base.supplierName : undefined) ?? null;
  // owner 2026-10-23 20:59: suppliers that have the item first (with their price); the others say „nie ma tej pozycji”
  // and stay selectable (then the message and „Dodaj w katalogu dostawcy”) — only for whoever sees the prices
  const knowsPositions = seePrices && draft.itemId !== '' && !!prices.data;
  // owner 2026-10-24 (pkt 3): the supplier's „Ilość posiadanych” — „ma N szt.” and „max N” (7a: only when seen)
  const stockOf = (supplierId: string) => (draft.itemId !== '' ? stockOfSupplier(supplierId, chosen, prices.data?.prices) : undefined);
  // „zmień dostępność”: whoever may add or change rentals and sees the suppliers' quantities (the server checks it too)
  const mayTypeAvailability = roomAsked && (can(me, RENTALS_RESOURCE, 'create') || can(me, RENTALS_RESOURCE, 'edit'));
  // the room's max when known (all events, these days); else the position's „Ilość posiadanych” as the form knows it
  const stock = roomAsked && room.data && 'max' in room.data ? room.data.max : draft.supplierId ? stockOf(draft.supplierId) : undefined;
  const supplierOptions = (suppliers.data?.contractors ?? [])
    .map((x) => ({ id: x.id, name: x.name, price: seePrices ? priceOf(x.id) : undefined, stock: stockOf(x.id), lacks: knowsPositions && priceOf(x.id) === undefined && !(chosen && chosen.supplierId === x.id) }))
    .sort((a, b) => Number(a.lacks) - Number(b.lacks));
  // after „Dodaj w katalogu dostawcy” „Wróć” comes back to this form (a new rental: what was typed, as „Wskaż w katalogu”)
  const here = `${location.pathname}${location.search}`;
  const lackBack = base ? here : rentalFormUrl(here, { itemId: draft.itemId, itemName: draft.itemName, quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1, firstDay: draft.firstDay, lastDay: draft.lastDay, carried: carriedOf(draft) });
  /** A rental without our item (a supplier's own position or a cost only, made from a quote). */
  const outside = !!base && !base.keptByQuote && (base.itemId === null || base.costOnly === true);
  /** What to send (only what changed), or null with the reason shown. */
  function fieldsToSend(): Record<string, unknown> | null {
    const fail = (why: string) => (setInvalid(why), null);
    setInvalid('');
    const off = draft.offList;
    if (sees('itemId') && draft.itemId === '' && !outside && !(off && draft.name.trim() !== '')) return fail(off ? 'Wybierz sprzęt z katalogu albo wpisz nazwę sprzętu' : 'Wybierz sprzęt z katalogu');
    if (sees('supplierId') && draft.supplierId === '' && !off) return fail('Wybierz dostawcę');
    if (sees('quantity') && !(Number.isInteger(quantity) && quantity > 0)) return fail('Ilość musi być liczbą całkowitą większą od zera');
    const overStock = sees('quantity') && draft.supplierId && !off ? stockProblem(quantity, stock, supplierName(draft.supplierId) ?? 'Dostawca') : null;
    if (overStock && (!base || quantity > (base.quantity ?? 0) || draft.supplierId !== base.supplierId)) return fail(overStock);
    let costGrosze: number | undefined;
    if (seeCost && (draft.costMode === 'reczny' || off)) {
      try {
        costGrosze = parsePln(draft.cost);
      } catch (error) {
        return fail(error instanceof Error ? error.message : 'Nieprawidłowa kwota');
      }
    }
    const original = draftOf(base, preset);
    const changed = (k: keyof Draft) => !base || draft[k] !== original[k];
    const fields: Record<string, unknown> = {
      ...(sees('itemId') && changed('itemId') ? { itemId: draft.itemId } : {}),
      ...(sees('supplierId') && changed('supplierId') && !off ? { supplierId: draft.supplierId } : {}),
      // owner 2026-10-24 16:15 (pkt 4): „Inny (spoza listy)” — no listed supplier, the typed cost, its name and contact
      ...(off && !base ? { offList: true, costMode: 'reczny' } : {}),
      ...(off && draft.itemId === '' && changed('name') ? { name: draft.name.trim() } : {}),
      ...(off ? Object.fromEntries((['offSupplierName', 'description', 'contactPhone', 'contactEmail', 'contactPerson'] as const).filter((k) => sees(k) && changed(k) && (base || draft[k].trim() !== '')).map((k) => [k, draft[k].trim()])) : {}),
      ...(sees('quantity') && changed('quantity') ? { quantity } : {}),
      ...(sees('firstDay') && changed('firstDay') ? { firstDay: draft.firstDay } : {}),
      ...(sees('lastDay') && changed('lastDay') ? { lastDay: draft.lastDay } : {}),
      ...(sees('costMode') && changed('costMode') && !off ? { costMode: draft.costMode } : {}),
      ...(costGrosze !== undefined && (!base || costGrosze !== base.costGrosze || changed('costMode')) ? { costGrosze } : {}),
      ...(sees('status') && changed('status') ? { status: draft.status } : {}),
      ...(sees('returnPersonId') && changed('returnPersonId') && (base || draft.returnPersonId !== '') ? { returnPersonId: draft.returnPersonId === '' ? null : draft.returnPersonId } : {}),
      ...(sees('notes') && changed('notes') && (base || draft.notes !== '') ? { notes: draft.notes } : {}),
      ...(matching && ofChosen && !off ? { priceId: chosen!.priceId, ...(mayLink && link ? { linkPrice: true } : {}) } : {}),
    };
    return fields;
  }
  /** The split's request (one rental per supplier with a quantity), or null with the reason shown. */
  function splitBody(): Record<string, unknown> | null {
    const fail = (why: string) => (setInvalid(why), null);
    setInvalid('');
    if (!needOk) return fail('Ilość musi być liczbą całkowitą większą od zera');
    const chosenRows = splitRows.filter((r) => (splitQty.get(r.priceId) ?? 0) > 0);
    if (chosenRows.length === 0) return fail('Wpisz ilość u co najmniej jednego dostawcy');
    for (const r of chosenRows) {
      const problem = stockProblem(splitQty.get(r.priceId)!, typeof r.available === 'number' ? r.available : undefined, r.supplierName);
      if (problem) return fail(problem);
    }
    const shared: Record<string, unknown> = { itemId: draft.itemId, firstDay: draft.firstDay, lastDay: draft.lastDay };
    if (sees('status')) shared.status = draft.status;
    if (sees('returnPersonId') && draft.returnPersonId !== '') shared.returnPersonId = draft.returnPersonId;
    if (sees('notes') && draft.notes !== '') shared.notes = draft.notes;
    const parts = chosenRows.map((r) => {
      const o = overrides[r.priceId];
      return {
        supplierId: r.supplierId,
        priceId: r.priceId,
        quantity: splitQty.get(r.priceId)!,
        ...(links.has(r.priceId) ? { linkPrice: true } : {}),
        ...(o?.open && o.firstDay ? { firstDay: o.firstDay } : {}),
        ...(o?.open && o.lastDay ? { lastDay: o.lastDay } : {}),
        ...(o?.open && o.status ? { status: o.status } : {}),
        ...(o?.open && o.returnPersonId ? { returnPersonId: o.returnPersonId } : {}),
        ...(o?.open && o.notes ? { notes: o.notes } : {}),
      };
    });
    return { ...shared, parts };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    if (splitOn) {
      if (splitBusy.current) return;
      const body = splitBody();
      if (!body) return;
      splitBusy.current = true;
      splitSave.mutate(body, { onSettled: () => void (splitBusy.current = false) });
      return;
    }
    const fields = fieldsToSend();
    if (!fields) return;
    if (base && Object.keys(fields).length === 0) return onClose();
    save.mutate(fields);
  }
  saveNow.current = async () => {
    if (splitOn) {
      if (splitBusy.current) return false;
      const body = splitBody();
      if (!body) return false;
      splitBusy.current = true;
      try {
        await splitSave.mutateAsync(body);
      } finally {
        splitBusy.current = false;
      }
      return true;
    }
    const fields = fieldsToSend();
    if (!fields) return false;
    if (!base || Object.keys(fields).length > 0) await save.mutateAsync(fields);
    return true;
  };

  const error = [save.error, splitSave.error, remove.error, detach.error].find((x) => x instanceof ApiError) as ApiError | undefined;
  const conflict = error?.status === 409;
  const reload = async () => {
    const fresh = await api<RentalView>(`/api/subrentals/rentals/${base!.id}`);
    // the other rentals of the order too (their versions go with „dla wszystkich pozycji”)
    await queryClient.invalidateQueries({ queryKey: SUBRENTALS_KEY });
    setBase(fresh);
    setDraft(draftOf(fresh));
    save.reset();
    remove.reset();
  };

  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel rental-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rental-title"
        data-feature-id="subrentals.tab"
        onSubmit={submit}
        noValidate
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={save.isPending || remove.isPending || detach.isPending} />
        <h2 id="rental-title">{base ? 'Dopożyczenie' : 'Dopożycz sprzęt'}</h2>
        {kept && (
          <p className="hint rental-kept" data-feature-id="quotes.suppliers">
            Z kosztorysu: <strong>{base?.itemName}</strong> × {base?.quantity} u {base?.supplierName}. Ilość, dostawcę, koszt i usunięcie zmienisz w kosztorysie wydarzenia — tu status, daty, uwagi i kto oddaje sprzęt.
          </p>
        )}
        {base?.status === 'anulowane' && (
          // owner 2026-11-01: cancelled with the event — only its notes change until the event is restored
          <p className="hint rental-cancelled-note" data-feature-id="subrentals.tab">
            <RentalMarkTag mark={base.mark} /> Dopożyczenie anulowano razem z wydarzeniem — nie liczy się w kosztach ani rozliczeniach.
            {base.cancelledFrom ? ` Po przywróceniu wydarzenia wróci do statusu „${RENTAL_STATUSES[base.cancelledFrom]}”.` : ''} Teraz zmienisz tylko uwagi. Program niczego nie wysyła dostawcy.
          </p>
        )}
        {!kept && base?.keptByQuote && base.status !== 'anulowane' && (
          <p className="hint rental-kept" data-feature-id="quotes.suppliers">
            {base.outsideQuote ? 'Z kosztorysu, ale tej pozycji nie ma już w obowiązującej wersji kosztorysu (albo kosztorys usunięto).' : 'Z kosztorysu, sprzęt już odebrany.'} Ilość, dostawcę i koszt zmieniasz tutaj.
          </p>
        )}
        {outside && (
          // owner 2026-10-21: a position only the supplier has, or "Sam koszt u dostawcy" (from the quote) — no item to pick
          <p className="rental-item">
            <span className="muted">{base?.costOnly ? 'Sam koszt' : 'Spoza naszego katalogu'}</span> <strong>{base?.itemName}</strong>
          </p>
        )}
        {sees('itemId') && !outside && (
          <div className="group">
            {draft.itemId ? (
              <p className="rental-item">
                <span className="muted">Sprzęt</span> <strong>{draft.itemName}</strong>{' '}
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => {
                    set({ itemId: '', itemName: '' });
                    setChosen(null);
                  }}
                >
                  Zmień
                </button>
              </p>
            ) : (
              <QuickPicker<ItemOption> label="Sprzęt z katalogu" placeholder="Szukaj w katalogu…" search={searchItems} onPick={(o, q) => set({ itemId: o.id, itemName: o.name, ...(q > 1 ? { quantity: String(q) } : {}) })} />
            )}
          </div>
        )}
        {splitOn && (
          <>
            <div className="field-row">
              <label className="field narrow-field">
                <span>Potrzeba (szt.)</span>
                <input inputMode="numeric" value={draft.quantity} onChange={(e) => set({ quantity: e.target.value })} />
              </label>
            </div>
            <RentalSplit
              itemId={draft.itemId}
              rows={splitRows}
              need={needOk ? need : 0}
              days={days}
              quantities={splitQty}
              onQuantity={(priceId, q) => {
                setSplitTouched(true);
                setSplitQty((m) => new Map(m).set(priceId, q));
              }}
              overrides={overrides}
              onOverride={(priceId, o) => setOverrides((all) => ({ ...all, [priceId]: o }))}
              mayLink={can(me, PRICES_RESOURCE, 'edit') && fieldVisible(me, PRICES_RESOURCE, 'itemId')}
              links={links}
              onLink={(priceId, on) =>
                setLinks((all) => {
                  const next = new Set(all);
                  if (on) next.add(priceId);
                  else next.delete(priceId);
                  return next;
                })
              }
              sees={sees}
              persons={persons.data?.persons}
              lacking={supplierOptions.filter((o) => o.lacks).map((o) => ({ id: o.id, name: o.name }))}
              back={lackBack}
              availability={can(me, RENTALS_RESOURCE, 'create') && fieldVisible(me, PRICES_RESOURCE, 'quantity') ? { firstDay: draft.firstDay, lastDay: draft.lastDay } : null}
            />
            <button type="button" className="btn small ghost rental-mode" onClick={() => setSingle(true)}>
              Inny dostawca / wpisz ręcznie
            </button>
          </>
        )}
        {!splitOn && splitPossible && (
          <button type="button" className="btn small ghost rental-mode" onClick={() => setSingle(false)}>
            Od kilku dostawców naraz
          </button>
        )}
        {!splitOn && matching && (
          <SupplierPositionPicker
            key={draft.itemId}
            itemId={draft.itemId}
            itemName={draft.itemName}
            eventId={eventId}
            chosen={chosen}
            onChoose={choose}
            auto={!picked}
            pickHref={rentalPickUrl(`${location.pathname}${location.search}`, { itemId: draft.itemId, itemName: draft.itemName, quantity: Number.isInteger(quantity) && quantity > 0 ? quantity : 1, firstDay: draft.firstDay, lastDay: draft.lastDay, carried: carriedOf(draft) })}
          />
        )}
        {!splitOn && matching && pointedNote && (
          <p className="alert" role="alert">
            {pointedNote}
          </p>
        )}
        {!splitOn && noLink && <p className="muted small">Tej pozycji nie połączysz z „{draft.itemName}”: {chosen!.supplierName ?? 'ten dostawca'} ma już w katalogu inną pozycję połączoną z tym sprzętem.</p>}
        {!splitOn && mayLink && (
          <label className="check rental-link">
            <input type="checkbox" checked={link} onChange={(e) => setLink(e.target.checked)} />
            <span>
              Połącz „{candidateName(chosen!)}”{chosen!.supplierName ? ` u ${chosen!.supplierName}` : ''} z moim „{draft.itemName}” (następnym razem znajdzie się od razu)
            </span>
          </label>
        )}
        {!splitOn && (
        <div className="field-row">
          {sees('supplierId') && (
            <label className="field">
              <span>Dostawca</span>
              <select
                value={draft.offList ? OFF : draft.supplierId}
                disabled={!!base?.offList}
                onChange={(e) => {
                  // owner 2026-10-24 16:15 (pkt 4): „Inny (spoza listy)” — the cost is typed (no catalog)
                  if (e.target.value === OFF) {
                    set({ offList: true, supplierId: '', costMode: 'reczny' });
                    setChosen(null);
                    return;
                  }
                  set({ supplierId: e.target.value, ...leaveOffList(draft) });
                  // another supplier: the chosen position (its price) is not theirs
                  if (chosen && chosen.supplierId !== e.target.value) setChosen(null);
                }}
                autoFocus={!!preset && !matching}
              >
                <option value="">Wybierz dostawcę</option>
                {(!base ? offAllowed : base.offList) && <option value={OFF}>Inny (spoza listy)</option>}
                {supplierOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.price !== undefined ? ` — ${formatPln(s.price)}/doba` : s.lacks ? ' — nie ma tej pozycji' : ''}
                    {stockLabel(s.stock) ? ` · ${stockLabel(s.stock)}` : ''}
                  </option>
                ))}
                {base?.supplierId && !(suppliers.data?.contractors ?? []).some((s) => s.id === base.supplierId) && <option value={base.supplierId}>{base.supplierName}</option>}
              </select>
            </label>
          )}
          {sees('quantity') && (
            <label className="field narrow-field">
              <span>
                Ilość
                {typeof stock === 'number' && (
                  <span className="muted small rental-max" title={roomTitle(room.data)}>
                    {' '}
                    max {stock}
                  </span>
                )}
              </span>
              {/* owner 2026-10-24 16:15: more than the max typed — the max (also for „my position”, the chosen one's stock) */}
              <input inputMode="numeric" value={draft.quantity} max={typeof stock === 'number' ? stock : undefined} onChange={(e) => set({ quantity: clampToMax(e.target.value, stock) })} onBlur={() => set({ quantity: clampToMax(draft.quantity, stock) })} />
            </label>
          )}
        </div>
        )}
        {/* owner 2026-10-24 16:15 (pkt 4): „Inny (spoza listy)” — the supplier's name, the gear's name / description; ⚙ contact */}
        {!splitOn && draft.offList && (
          <div className="group rental-off-list" data-testid="rental-off-list">
            <div className="field-row">
              {sees('offSupplierName') && (
                <label className="field">
                  <span>Dostawca (spoza listy)</span>
                  <input value={draft.offSupplierName} maxLength={200} placeholder="np. nazwa firmy albo osoby" onChange={(e) => set({ offSupplierName: e.target.value })} />
                </label>
              )}
              <button type="button" className="btn small ghost rental-gear" aria-expanded={gear} title="Kontakt do dostawcy" aria-label="Kontakt do dostawcy (telefon, e-mail, osoba)" onClick={() => setGear((g) => !g)}>
                ⚙
              </button>
            </div>
            {(draft.itemId === '' || outside) && sees('name') && (
              <label className="field">
                <span>Nazwa sprzętu</span>
                <input value={draft.name} maxLength={300} onChange={(e) => set({ name: e.target.value })} />
              </label>
            )}
            {sees('description') && (
              <label className="field">
                <span>Opis sprzętu</span>
                <input value={draft.description} maxLength={2000} placeholder="opisz po swojemu, np. dwa suby z kablami i pokrowcami" onChange={(e) => set({ description: e.target.value })} />
              </label>
            )}
            {gear && (
              <div className="field-row rental-contact">
                {sees('contactPhone') && (
                  <label className="field">
                    <span>Telefon</span>
                    <input inputMode="tel" value={draft.contactPhone} maxLength={50} onChange={(e) => set({ contactPhone: e.target.value })} />
                  </label>
                )}
                {sees('contactEmail') && (
                  <label className="field">
                    <span>E-mail</span>
                    <input type="email" value={draft.contactEmail} maxLength={200} onChange={(e) => set({ contactEmail: e.target.value })} />
                  </label>
                )}
                {sees('contactPerson') && (
                  <label className="field">
                    <span>Osoba</span>
                    <input value={draft.contactPerson} maxLength={200} onChange={(e) => set({ contactPerson: e.target.value })} />
                  </label>
                )}
              </div>
            )}
          </div>
        )}
        {/* owner 2026-10-24 16:15: „zmień dostępność” — the supplier's quantity on these days, typed by hand */}
        {!splitOn && sees('quantity') && mayTypeAvailability && room.data?.positionId && (
          <p className="rental-availability-row muted small">
            {room.data.manual && room.data.manual.length > 0 ? 'Dostępność u dostawcy w tych dniach wpisana ręcznie.' : 'Dostawca ma w tych dniach inną ilość niż w katalogu?'}
            <AvailabilityEditor key={`${room.data.positionId}:${draft.firstDay}:${draft.lastDay}`} positionId={room.data.positionId} firstDay={draft.firstDay} lastDay={draft.lastDay} manual={room.data.manual ?? []} />
          </p>
        )}
        {!splitOn && sees('supplierId') && !draft.offList && (
          <BarterWarning
            contractorId={draft.supplierId || null}
            contractorName={(suppliers.data?.contractors ?? []).find((s) => s.id === draft.supplierId)?.name ?? base?.supplierName ?? 'tego dostawcy'}
          />
        )}
        {base && group.length > 0 && !supplierChanged && GROUP_FIELDS.some((f) => sees(f)) && (
          <div className="rental-for-all">
            <label className="check">
              <input type="checkbox" checked={forAll} aria-describedby="rental-for-all-note" onChange={(e) => setForAll(e.target.checked)} />
              <span>Zmień dla wszystkich pozycji{base.supplierName ? ` od ${base.supplierName}` : ' od tego dostawcy'} w tym wydarzeniu</span>
            </label>
            <p id="rental-for-all-note" className="muted small">
              Kto oddaje sprzęt, daty, status i uwagi. Sprzęt, ilość i wpisany koszt zmieniają się tylko w tej pozycji; zmiana dni przelicza koszt pozycji liczonych z katalogu dostawcy (cena za dobę).
            </p>
          </div>
        )}
        {(sees('firstDay') || sees('lastDay')) && (
          <div className="field-row">
            {sees('firstDay') && (
              <label className="field">
                <span>Pierwszy dzień (odbiór)</span>
                <input type="date" value={draft.firstDay} onChange={(e) => set({ firstDay: e.target.value, ...(draft.lastDay < e.target.value ? { lastDay: e.target.value } : {}) })} />
              </label>
            )}
            {sees('lastDay') && (
              <label className="field">
                <span>Ostatni dzień (zwrot)</span>
                <input type="date" value={draft.lastDay} min={draft.firstDay || undefined} onChange={(e) => set({ lastDay: e.target.value })} />
              </label>
            )}
          </div>
        )}
        {!splitOn && sees('costMode') && seeCost && (
          <div className="group">
            {!draft.offList && <div className="segment" role="radiogroup" aria-label="Koszt">
              {(['cennik', 'reczny'] as const).map((m) => (
                <button key={m} type="button" role="radio" aria-checked={draft.costMode === m} aria-pressed={draft.costMode === m} onClick={() => set({ costMode: m })}>
                  {m === 'cennik' ? 'Z katalogu dostawcy' : 'Wpisany ręcznie'}
                </button>
              ))}
            </div>}
            {draft.costMode === 'cennik' && !draft.offList ? (
              <p className="muted small rental-cost-note">
                {!draft.supplierId || !draft.itemId
                  ? 'Koszt policzy się z ceny dostawcy za dobę × ilość × liczba dni.'
                  : supplierPrice === undefined
                    ? seePrices && prices.data
                      ? <><SupplierLacksItem supplierId={draft.supplierId} supplierName={supplierName(draft.supplierId)} itemId={draft.itemId} back={lackBack} /> Wybierz innego dostawcę albo wpisz koszt ręcznie.</>
                      : 'Koszt policzy się z katalogu dostawcy.'
                    : `${formatPln(supplierPrice)} × ${draft.quantity} szt. × ${days ?? '?'} ${days === 1 ? 'dzień' : 'dni'}${estimate !== null ? ` = ${formatPln(estimate)}` : ''}`}
              </p>
            ) : (
              <label className="field narrow-field">
                <span>Koszt (zł)</span>
                <input inputMode="decimal" value={draft.cost} onChange={(e) => set({ cost: e.target.value })} onBlur={() => { try { set({ cost: plnToInput(parsePln(draft.cost)) }); } catch { /* shown on save */ } }} />
              </label>
            )}
          </div>
        )}
        <div className="field-row">
          {sees('status') && (
            <label className="field">
              <span>Status</span>
              <select value={draft.status} disabled={draft.status === 'anulowane'} onChange={(e) => set({ status: e.target.value as RentalStatus })}>
                {/* owner 2026-11-01: „Anulowane” is the program's — shown only as the current status */}
                {(draft.status === 'anulowane' ? (['anulowane'] as RentalStatus[]) : MANUAL_STATUSES).map((s) => (
                  <option key={s} value={s}>
                    {RENTAL_STATUSES[s]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {sees('returnPersonId') && persons.data && (
            <label className="field">
              <span>Kto oddaje sprzęt</span>
              <select value={draft.returnPersonId} onChange={(e) => set({ returnPersonId: e.target.value })}>
                <option value="">— nikt</option>
                {persons.data.persons.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {sees('returnPersonId') && draft.returnPersonId !== '' && <p className="muted small">Ta osoba dostanie zadanie „Zwrot wypożyczenia” na ostatni dzień, z przypomnieniami aż do odhaczenia.</p>}
        {sees('notes') && (
          <label className="field">
            <span>Uwagi</span>
            <NotesInput maxLength={2000} value={draft.notes} placeholder="np. odbiór o 10:00, kontakt" onChange={(e) => set({ notes: e.target.value })} />
          </label>
        )}
        {invalid && (
          <p className="alert" role="alert">
            {invalid}
          </p>
        )}
        {conflict && <ConflictNotice onReload={() => void reload()} />}
        {error && !conflict && error.code === 'supplier_lacks_item' && (
          // owner 2026-10-23 20:59: nothing saved — the supplier lacks the position (named as the server may name it)
          <p className="alert" role="alert">
            <SupplierLacksItem
              supplierId={(error.details as { supplierId?: string } | undefined)?.supplierId}
              supplierName={(error.details as { supplierId?: string } | undefined)?.supplierId ? supplierName((error.details as { supplierId: string }).supplierId) : null}
              itemId={(error.details as { itemId?: string } | undefined)?.itemId}
              back={lackBack}
            />
          </p>
        )}
        {error && !conflict && error.code !== 'supplier_lacks_item' && (
          <p className="alert" role="alert">
            {fieldIssues(error).length > 0 ? fieldIssues(error).map((i) => i.message).join(' ') : error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending || splitSave.isPending}>
            {save.isPending || splitSave.isPending ? 'Zapisywanie…' : base ? 'Zapisz' : 'Dopożycz'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
          {detachable && can(me, RENTALS_RESOURCE, 'edit') && (
            <button className="btn ghost" type="button" disabled={detach.isPending} title="Dopożyczenie przestaje być związane z kosztorysem: kosztorys już go nie zmieni." onClick={() => detach.mutate()}>
              Odłącz od kosztorysu
            </button>
          )}
          {base && !kept && can(me, RENTALS_RESOURCE, 'delete') && (
            <button className="btn ghost danger" type="button" disabled={remove.isPending} onClick={() => (deleting ? remove.mutate() : setDeleting(true))}>
              {deleting ? 'Na pewno usunąć?' : 'Usuń'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
